import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useDraftsStore } from '@/stores/drafts'
import { useRemoteImportStore } from '@/stores/remote-import'
import { useAmazonCoversStore } from '@/stores/amazon-covers'
import { usePrintBooksStore } from '@/stores/print-books'
import { useEditorStore } from '@/stores/editor'
import { useTranscriptionStore } from '@/stores/transcription'
import { amazonCoverImagePath } from '@/helpers/coverImage'
import { useUserStore } from '@/stores/user'
import { http } from '@/helpers/http'
import { track } from '@/helpers/mixpanelDes'
import { tipTapDocToEpubData } from '@/helpers/docToEpub.ts'
import type { TDevice } from '@/types/kindle-books.ts'
import type { TAmazonElementText } from '@/types/amazon-cover.ts'

export const useKindleBooksStore = defineStore('kindle-books', () => {
  const router = useRouter()
  const drafts = useDraftsStore()
  const remoteImports = useRemoteImportStore()
  const covers = useAmazonCoversStore()
  const printBooks = usePrintBooksStore()
  const userStore = useUserStore()
  const imageBaseUrl = '/dashboard/devices'
  const contentScrollTop = ref<number>(0)
  const displayMode = ref<'cover' | 'content'>('cover')
  const rotated = ref<boolean>(false)
  const previewFontSize = ref<number>(25)

  const devices: TDevice[] = [
    {
      label: 'ink',
      screenWPct: { normal: 80, rotated: 120 },
      screenHPct: { normal: 92, rotated: 62 },
      screenTopPct: { normal: 4, rotated: 16 },
      screenLeftPct: { normal: 14, rotated: -10 },
      rotatedContentWidthPx: 1500,
      icon: 'fa-light fa-tablet-screen-button',
      image: `${imageBaseUrl}/ink.png`,
    },
    {
      label: 'kindle',
      screenWPct: { normal: 74, rotated: 118 },
      screenHPct: { normal: 83, rotated: 52 },
      screenTopPct: { normal: 6, rotated: 24 },
      screenLeftPct: { normal: 13, rotated: -13 },
      rotatedContentWidthPx: 1600,
      icon: 'fa-light fa-tablet',
      image: `${imageBaseUrl}/kindle.png`,
    },
    {
      label: 'mobile',
      screenWPct: { normal: 64, rotated: 136 },
      screenHPct: { normal: 100, rotated: 46 },
      screenTopPct: { normal: 0, rotated: 27 },
      screenLeftPct: { normal: 18, rotated: -18 },
      rotatedContentWidthPx: 2125,
      icon: 'fa-light fa-mobile',
      image: `${imageBaseUrl}/iphone.png`,
    },
  ]

  const chosenDevice = ref<TDevice>(devices[1]!) // kindle by default

  const pendingDownload = ref<boolean>(false)
  const pendingDescription = ref<string>('')
  const pendingHideCover = ref<boolean>(false)

  function setPendingDownload(description = '', hideCover = false): void {
    pendingDescription.value = description
    pendingHideCover.value = hideCover
    pendingDownload.value = true
  }

  function clearPendingDownload(): void {
    pendingDownload.value = false
  }

  const stepUrlMap = useLocalStorage<Record<number, string>>('kindle-book-step-urls', {})

  function setContentScrollTop(top: number) {
    contentScrollTop.value = top
  }

  function setDisplayMode(mode: 'cover' | 'content'): void {
    displayMode.value = mode
  }

  function setDevice(name: string): void {
    const device: TDevice | undefined = devices.find((d) => d.label === name)
    if (device) {
      chosenDevice.value = device
    } else {
      console.warn(`Device with label "${name}" not found`)
    }
  }

  function toggleRotated(): void {
    rotated.value = !rotated.value
  }

  function updateStepURL(kindleBookId?: number): void {
    const id = kindleBookId ?? drafts.draft?.kindle_book?.id
    if (!id) return
    stepUrlMap.value[id] = router.currentRoute.value.fullPath
  }

  function getCoverAuthor(): string {
    const text = (covers.cover?.content?.find((e) => e.type === 'author') as TAmazonElementText | undefined)
      ?.textContent
    return text?.trim() || ''
  }

  async function createFromDraft(draftId: number): Promise<number | null> {
    const editorStore = useEditorStore()
    const transcriptions = useTranscriptionStore()
    const draft = drafts.draft?.id === draftId ? drafts.draft : await drafts.get(draftId)

    if (!draft?.id) throw new Error('Failed to load the manuscript')

    const content = transcriptions.draftToProjectContent(draft, editorStore.getEditor())

    try {
      const newId = await drafts.save(
        draft.id,
        draft.name ?? '',
        remoteImports.insertPageBreaksBeforeHeadings(content),
        draft.footer_url ?? '',
        draft.import_type,
        draft.import_source,
        draft.tags,
        undefined,
        true,
        draft.print_book ?? null,
        null,
        true,
      )

      track('kindle-book-created-from-draft', { draftId })
      return newId ?? null
    } catch {
      track('kindle-book-create-from-draft-failed', { draftId })
      throw new Error('Failed to create Kindle book')
    }
  }

  async function coverSource(): Promise<string | undefined> {
    if (pendingHideCover.value || !covers.cover?.thumbnail) return undefined

    const url = location.origin + amazonCoverImagePath(covers.cover.id, covers.cover.image_url)

    return (await printBooks.toDataURL(url)) ?? url
  }

  async function generateEpub(): Promise<string | null> {
    if (!drafts.draft?.id) return null

    const chapterInfo = tipTapDocToEpubData(drafts.draft.content)
    if (!chapterInfo) return null

    const coverImageUrl = await coverSource()

    const res = await http.post<{ url: string }>(
      '/epub/kindle',
      {
        html: chapterInfo.chapters.map((c) => c.outerHTML).join(''),
        title: drafts.draft?.name ?? 'Kindle Book',
        author: getCoverAuthor() || userStore.getUserFullNameOrEmail(),
        description: pendingDescription.value,
        fontSize: previewFontSize.value,
        ...(coverImageUrl && { coverUrl: coverImageUrl }),
      },
      false,
      15 * 60 * 1000,
    )

    return res?.url ?? null
  }

  async function generateKindleBundle(coverId?: number): Promise<string | null> {
    if (!drafts.draft?.id) {
      return null
    }

    const chapterInfo = tipTapDocToEpubData(drafts.draft.content)
    if (!chapterInfo) {
      return null
    }

    const coverImageUrl = await coverSource()

    const res = await http.post<{ url: string }>(
      '/epub/kindle-zip',
      {
        html: chapterInfo.chapters.map((c) => c.outerHTML).join(''),
        title: drafts.draft?.name ?? 'kindle-book',
        author: getCoverAuthor() || userStore.getUserFullNameOrEmail(),
        description: pendingDescription.value,
        fontSize: previewFontSize.value,
        ...(coverImageUrl && { coverUrl: coverImageUrl }),
        ...(coverId && { coverId }),
      },
      false,
      15 * 60 * 1000,
    )

    return res?.url ?? null
  }

  function getStepURL(kindleBookId: number): string | null {
    return stepUrlMap.value?.[kindleBookId] ?? null
  }

  return {
    devices,
    chosenDevice,
    setDevice,
    displayMode,
    setDisplayMode,
    contentScrollTop,
    setContentScrollTop,
    toggleRotated,
    previewFontSize,
    rotated,
    updateStepURL,
    getStepURL,
    pendingDownload,
    pendingDescription,
    pendingHideCover,
    setPendingDownload,
    clearPendingDownload,
    generateEpub,
    generateKindleBundle,
    createFromDraft,
  }
})
